
import { useCallback, useState, useEffect } from "react";
import { useAccount } from "wagmi";
import { useWeb3Modal } from "@web3modal/wagmi/react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { DaoDetails } from "@/components/dao-it/DaoDetails";
import { ExternalLink, ArrowRight, Archive, ArchiveRestore, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { isAddress } from "viem";

interface DaoData {
  id: string;
  name: string;
  symbol: string;
  creatorAddress: string;
  governorAddress: string;
  tokenAddress: string;
  treasuryAddress: string;
  createdAt: string;
  chainId: number;
  chainName: string;
  deployerUserId: string | null;
  archivedAt: string | null;
}

export const ManageDaoForm = () => {
  const { address, isConnected } = useAccount();
  const { open } = useWeb3Modal();
  const { toast } = useToast();
  const [daoAddress, setDaoAddress] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [daoLoaded, setDaoLoaded] = useState(false);
  const [daoData, setDaoData] = useState<DaoData | null>(null);
  const [userDaos, setUserDaos] = useState<DaoData[]>([]);
  const [loadingUserDaos, setLoadingUserDaos] = useState(false);
  const [authenticatedUserId, setAuthenticatedUserId] = useState<string | null>(null);
  const [updatingDaoId, setUpdatingDaoId] = useState<string | null>(null);

  const loadUserDaos = useCallback(async () => {
    if (!address) {
      setUserDaos([]);
      return;
    }

    setLoadingUserDaos(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setAuthenticatedUserId(user?.id ?? null);

      const { data, error } = await supabase
        .from("daos")
        .select("*")
        .eq("creator_address", address.toLowerCase())
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error loading user DAOs:", error);
        toast({
          title: "Error loading DAOs",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      if (data) {
        setUserDaos(data.map((dao) => ({
            id: dao.id,
            name: dao.name,
            symbol: dao.symbol,
            creatorAddress: dao.creator_address,
            tokenAddress: dao.token_address,
            governorAddress: dao.governor_address,
            treasuryAddress: dao.treasury_address,
            chainId: 137,
            chainName: "Polygon",
            createdAt: dao.created_at,
            deployerUserId: dao.deployer_user_id,
            archivedAt: dao.archived_at,
          })));
      }
    } catch (error) {
      console.error("Error loading user DAOs:", error);
    } finally {
      setLoadingUserDaos(false);
    }
  }, [address, toast]);

  // Load DAOs created by the connected wallet and refresh after deployments.
  useEffect(() => {
    void loadUserDaos();

    const handleDaoCreated = () => {
      console.log("DAO created event received, refreshing list...");
      void loadUserDaos();
    };

    window.addEventListener("daoCreated", handleDaoCreated);
    return () => window.removeEventListener("daoCreated", handleDaoCreated);
  }, [loadUserDaos]);

  const handleArchiveChange = async (dao: DaoData, restore: boolean) => {
    if (!authenticatedUserId || dao.deployerUserId !== authenticatedUserId) {
      toast({
        title: "Account not authorized",
        description: `Sign in with the account that deployed this DAO to ${restore ? "restore" : "archive"} it.`,
        variant: "destructive",
      });
      return;
    }

    setUpdatingDaoId(dao.id);
    try {
      const archivedAt = restore ? null : new Date().toISOString();
      const { data, error } = await supabase
        .from("daos")
        .update({ archived_at: archivedAt })
        .eq("id", dao.id)
        .select("id")
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error("The authenticated account is not allowed to update this DAO.");

      setUserDaos((current) => current.map((item) => (
        item.id === dao.id
          ? { ...item, archivedAt }
          : item
      )));
      toast({
        title: restore ? "DAO restored" : "DAO archived",
        description: `${dao.name} is now shown in ${restore ? "Active" : "Archived"} DAOs. Its on-chain contracts were not changed.`,
      });
    } catch (error) {
      console.error(`Error ${restore ? "restoring" : "archiving"} DAO:`, error);
      toast({
        title: `Unable to ${restore ? "restore" : "archive"} DAO`,
        description: error instanceof Error ? error.message : "The database rejected the update.",
        variant: "destructive",
      });
    } finally {
      setUpdatingDaoId(null);
    }
  };

  const handleLoadDao = async () => {
    if (!isConnected) {
      open();
      return;
    }

    const trimmedAddress = daoAddress.trim();
    if (!trimmedAddress || !isAddress(trimmedAddress)) {
      toast({
        title: "Error",
        description: "Please enter a valid DAO governor address",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      // Fetch DAO from Supabase by governor address
      const { data: dao, error } = await supabase
        .from("daos")
        .select("*")
        .eq("governor_address", trimmedAddress.toLowerCase())
        .maybeSingle();

      if (error || !dao) {
        toast({
          title: "DAO Not Found",
          description: "No DAO found with this governor address. Make sure it was created through this platform.",
          variant: "destructive",
        });
        return;
      }

      // Format DAO data
      const formattedDao: DaoData = {
        id: dao.id,
        name: dao.name,
        symbol: dao.symbol,
        creatorAddress: dao.creator_address,
        governorAddress: dao.governor_address,
        tokenAddress: dao.token_address,
        treasuryAddress: dao.treasury_address,
        createdAt: dao.created_at,
        chainId: 137, // Polygon mainnet
        chainName: "Polygon",
        deployerUserId: dao.deployer_user_id,
        archivedAt: dao.archived_at,
      };

      setDaoData(formattedDao);
      setDaoLoaded(true);
      
      toast({
        title: "DAO Loaded",
        description: `Successfully loaded ${formattedDao.name} (${formattedDao.symbol})`,
      });
    } catch (error) {
      console.error("Error loading DAO:", error);
      toast({
        title: "Error",
        description: "Failed to load DAO. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectDao = (dao: DaoData) => {
    setDaoData(dao);
    setDaoLoaded(true);
  };

  const renderDaoList = (daos: DaoData[], archived: boolean) => {
    if (daos.length === 0) {
      return (
        <div className="py-8 text-center text-sm text-muted-foreground">
          {archived ? "You have no archived DAOs." : "You have no active DAOs."}
        </div>
      );
    }

    return (
      <div className="space-y-2 mb-6">
        {daos.map((dao) => {
          const canUpdate = Boolean(authenticatedUserId && dao.deployerUserId === authenticatedUserId);
          const isUpdating = updatingDaoId === dao.id;
          return (
            <div
              key={dao.id}
              className="p-4 border rounded-lg hover:bg-accent/50 cursor-pointer transition-colors"
              onClick={() => handleSelectDao(dao)}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <h4 className="font-medium truncate">{dao.name}</h4>
                  <p className="text-sm text-muted-foreground">
                    {dao.symbol} • Created {new Date(dao.createdAt).toLocaleDateString()}
                  </p>
                  {!canUpdate && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Archive controls require the account used during deployment.
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!canUpdate || isUpdating}
                    title={!canUpdate ? "Sign in with the account used to deploy this DAO" : undefined}
                    onClick={(event) => {
                      event.stopPropagation();
                      void handleArchiveChange(dao, archived);
                    }}
                  >
                    {isUpdating ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : archived ? (
                      <ArchiveRestore className="mr-2 h-4 w-4" />
                    ) : (
                      <Archive className="mr-2 h-4 w-4" />
                    )}
                    {archived ? "Restore" : "Archive"}
                  </Button>
                  <ArrowRight className="h-5 w-5 text-muted-foreground" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {!daoLoaded ? (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Your DAOs</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={loadUserDaos}
                disabled={loadingUserDaos || !isConnected}
              >
                {loadingUserDaos ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <svg
                      className="mr-2 h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
                    </svg>
                    Refresh
                  </>
                )}
              </Button>
            </div>

            {/* Show user's DAOs if they have any */}
            {loadingUserDaos ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : userDaos.length > 0 ? (
              <>
                <Tabs defaultValue="active" className="mb-6">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="active">
                      Active ({userDaos.filter((dao) => !dao.archivedAt).length})
                    </TabsTrigger>
                    <TabsTrigger value="archived">
                      Archived ({userDaos.filter((dao) => dao.archivedAt).length})
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="active" className="mt-4">
                    {renderDaoList(userDaos.filter((dao) => !dao.archivedAt), false)}
                  </TabsContent>
                  <TabsContent value="archived" className="mt-4">
                    {renderDaoList(userDaos.filter((dao) => dao.archivedAt), true)}
                  </TabsContent>
                </Tabs>
                <div className="pt-4 border-t">
                  <p className="text-sm text-muted-foreground mb-2">Or load a DAO by governor address:</p>
                </div>
              </>
            ) : (
              <div className="py-8 text-center">
                <p className="text-muted-foreground mb-4">
                  {!isConnected 
                    ? "Connect your wallet to view your DAOs" 
                    : "No DAOs found for your wallet address"}
                </p>
                {isConnected && (
                  <p className="text-sm text-muted-foreground">
                    Create your first DAO or load an existing one by address below
                  </p>
                )}
              </div>
            )}

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="dao-address">DAO Governor Address</Label>
                <div className="flex gap-2">
                  <Input
                    id="dao-address"
                    placeholder="0x..."
                    value={daoAddress}
                    onChange={(e) => setDaoAddress(e.target.value)}
                  />
                  <Button 
                    onClick={handleLoadDao}
                    disabled={isLoading}
                  >
                    {isLoading ? "Loading..." : "Load"}
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Enter the governor contract address of your DAO
                </p>
              </div>
              
              <div className="pt-4">
                <div className="bg-muted/40 p-4 rounded-md">
                  <h3 className="font-medium mb-2">Don't have a DAO yet?</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Create a new DAO with our simple wizard in just a few steps.
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={() => window.location.hash = "#create"} 
                    className="w-full"
                  >
                    Create New DAO
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="pt-4">
                <h3 className="text-sm font-medium mb-2">External Governance Tools</h3>
                <a 
                  href="https://withtally.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-primary hover:underline text-sm"
                >
                  Manage your DAO with Tally
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        daoData && <DaoDetails dao={daoData} />
      )}
    </div>
  );
};
